import {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import StyledReactModal from "styled-react-modal";
import { createFocusTrap, FocusTrap } from "focus-trap";
import _uniqueId from "lodash/uniqueId";
import { ModalContextProvider } from "./ModalContext";
import { Title, Content, Actions, CloseButton } from "./components";
import { alpha } from "../../typography";
import "../../designTokens/variables.css";

type StyledReactModalDefault = {
  default: typeof StyledReactModal;
};

const getStyledReactModal = () => {
  if (Object.keys(StyledReactModal).includes("default")) {
    return (StyledReactModal as unknown as StyledReactModalDefault).default;
  }
  return StyledReactModal;
};

const StyledModal = getStyledReactModal().styled`
    display: flex;
    flex-direction: column;
    min-width: 500px;
    max-width: 80vw;
    max-height: 80vh;
    overflow: hidden;
    padding: 16px 0px;
    background-color: var(--color-neutral-000-full);
    box-shadow: var(--elevation-8);
    border-radius: var(--size-border-radius-medium);
    ${alpha.primaryActive.body3};
    > * {
        margin-bottom: 8px;
        padding: 0px 16px;
        :last-child {
            margin-bottom: 0px;
        }
    }
`;

type BaseModalProps = ComponentProps<typeof StyledReactModal>;

export enum LegacyModalTestIds {
  Container = "legacy-modal",
}

type ModalProps = {
  afterClose?: BaseModalProps["afterClose"];
  afterOpen?: BaseModalProps["afterOpen"];
  beforeClose?: BaseModalProps["beforeClose"];
  beforeOpen?: BaseModalProps["beforeOpen"];
  isOpen: boolean;
  disableBackgroundClick?: boolean;
  onClose: () => void;
  children: ReactNode;
  id?: string;
  testId?: string;
  className?: string;
  /** Accessible name for the dialog. Recommended whenever the modal doesn't render a `<LegacyModal.Title>`. */
  ariaLabel?: string;
};

export const LegacyModal = ({
  isOpen,
  onClose,
  children,
  className,
  testId,
  id,
  disableBackgroundClick,
  ariaLabel,
  afterOpen,
  afterClose,
  ...modalProps
}: ModalProps) => {
  const [generatedId] = useState(_uniqueId("legacy-modal-"));
  const containerId = id ?? generatedId;

  const focusTrapRef = useRef<FocusTrap | null>(null);

  // Kept in refs (rather than effect deps) so the handlers below can keep a stable identity
  // across re-renders: styled-react-modal re-invokes afterOpen/afterClose whenever their
  // reference changes, so a new function on every render would re-trigger the focus trap.
  const afterOpenRef = useRef(afterOpen);
  afterOpenRef.current = afterOpen;
  const afterCloseRef = useRef(afterClose);
  afterCloseRef.current = afterClose;

  const handleAfterOpen = useCallback(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    focusTrapRef.current = createFocusTrap(container, {
      fallbackFocus: container,
      escapeDeactivates: false,
      clickOutsideDeactivates: false,
      allowOutsideClick: true,
    });
    focusTrapRef.current.activate();
    afterOpenRef.current?.();
  }, [containerId]);

  const handleAfterClose = useCallback(() => {
    focusTrapRef.current?.deactivate();
    focusTrapRef.current = null;
    afterCloseRef.current?.();
  }, []);

  useEffect(() => {
    return () => {
      focusTrapRef.current?.deactivate();
      focusTrapRef.current = null;
    };
  }, []);

  return (
    <ModalContextProvider value={{ onClose }}>
      <StyledModal
        isOpen={isOpen}
        {...(!disableBackgroundClick && { onBackgroundClick: onClose })}
        onEscapeKeydown={onClose}
        afterOpen={handleAfterOpen}
        afterClose={handleAfterClose}
        // @ts-expect-error ts(2769) library styled-react-modal doesn't expect className but it still works
        className={className}
        data-testid={testId ?? LegacyModalTestIds.Container}
        id={containerId}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        {...modalProps}
      >
        {children}
      </StyledModal>
    </ModalContextProvider>
  );
};

LegacyModal.Title = Title;
LegacyModal.Content = Content;
LegacyModal.Actions = Actions;
LegacyModal.CloseButton = CloseButton;
